import { toast } from "react-toastify";
import { environment } from "../config/environment";
import { authService } from "../services/auth.service";

interface RequestConfig extends RequestInit {
  requiresAuth?: boolean;
  skipRefreshToken?: boolean;
  isFormData?: boolean;
  responseType?: 'json' | 'blob';
}

class ApiInterceptor {
  private static instance: ApiInterceptor;
  private isRefreshing = false;
  private refreshQueue: Array<(token: string) => void> = [];
  // private sessionTimeoutId: NodeJS.Timeout | null = null;
  // private readonly SESSION_TIMEOUT = 5 * 60 * 1000; // 5 minutes in milliseconds

  private constructor() {
  }

  static getInstance(): ApiInterceptor {
    if (!this.instance) {
      this.instance = new ApiInterceptor();
    }
    return this.instance;
  }

  /* private setupSessionTimeout() {
    // Skip session timeout setup in service worker context
    if (typeof window === 'undefined') {
      return;
    }
    // Reset timeout on any user activity
    const resetTimeout = () => {
      if (this.sessionTimeoutId) {
        clearTimeout(this.sessionTimeoutId);
      }
      this.sessionTimeoutId = setTimeout(this.handleSessionTimeout.bind(this), this.SESSION_TIMEOUT);
    };

    // Add event listeners for user activity
    ['mousedown', 'keydown', 'scroll', 'touchstart'].forEach(event => {
      window.addEventListener(event, resetTimeout);
    });

    // Initial setup
    resetTimeout();
  }

  private async handleSessionTimeout() {
    // Skip session timeout in service worker context
    if (typeof window === 'undefined') {
      return;
    }
    const isTokenExpired = await authService.isTokenExpired();
    if (isTokenExpired) {
      const newToken = await this.refreshTokenSilently();
      if (!newToken) {
        await authService.clearAllData();
        // Don't redirect automatically, let the auth service handle it
      }
    }
  } */

  private async refreshTokenSilently(): Promise<string | null> {
    try {
      return await authService.refreshAccessToken();
    } catch (error) {
      console.error('Silent token refresh failed:', error);
      return null;
    }
  }

  private async getHeaders(requiresAuth: boolean = true, isFormData: boolean = false, responseType: string = 'json'): Promise<Headers> {
    const headers = new Headers();

    // Only set Content-Type for non-FormData requests and non-blob requests
    if (!isFormData && responseType !== 'blob') {
      headers.set('Content-Type', 'application/json');
    }

    // Set Accept header based on response type
    if (responseType === 'blob') {
      headers.set('Accept', 'application/octet-stream');
    } else {
      headers.set('Accept', 'application/json');
    }

    if (requiresAuth) {
      let token = await authService.getToken();

      if (!token || await authService.isTokenExpired()) {
        token = await this.refreshTokenSilently();
      }

      if (token) {
        headers.append('Authorization', `Bearer ${token}`);
      }
    }

    return headers;
  }

  private enqueueRequest(request: () => Promise<any>): Promise<any> {
    return new Promise((resolve, reject) => {
      this.refreshQueue.push((token: string) => {
        request()
          .then(resolve)
          .catch(reject);
      });
    });
  }

  async fetch<T>(endpoint: string, config: RequestConfig = {}): Promise<T> {
    const { requiresAuth = true, skipRefreshToken = false, isFormData = false, responseType = 'json', ...fetchConfig } = config;

    const executeRequest = async () => {
      // Check if running in service worker environment
      const isServiceWorker = typeof window === 'undefined';

      // Check if the browser is offline
      const isOffline = typeof navigator !== 'undefined' && !navigator.onLine;

      if (isOffline && !isServiceWorker) {
        console.log(`[ApiInterceptor] Offline mode detected for request: ${endpoint}`);
      }
      
      const headers = await this.getHeaders(requiresAuth, isFormData);
      
      if(`${environment.apiUrl}${endpoint}`.includes('jobsite-management/get-jobsites') || `${environment.apiUrl}${endpoint}`.includes('users/getUsers')) {
        headers.set('Content-Type', 'application/x-www-form-urlencoded');
      }
      
      try {
        
        // Use the Request object with mode: 'cors' to ensure service worker can intercept
        const request = new Request(`${environment.apiUrl}${endpoint}`, {
          ...fetchConfig,
          headers: { ...Object.fromEntries(headers), ...fetchConfig.headers },
          // Make sure we're using the same mode as the service worker expects
          mode: 'cors',
        });

        const response = await fetch(request);

        if (!response.ok) {
          // For service worker requests, don't attempt auth refresh
          if (isServiceWorker) {
            const errorData = await response.json().catch(() => ({ message: 'Request failed' }));
            throw new Error(errorData.message || 'Request failed');
          }

          const errorData = await response.json();

          // Don't attempt token refresh for password reset related endpoints
          if (endpoint.includes('/auth/reset-password') ||
            endpoint.includes('/auth/verify-reset-token')) {
            throw new Error(errorData.message || 'Request failed');
          }

          if (response.status === 401 && !skipRefreshToken) {
            if (this.isRefreshing) {
              // Wait for the ongoing refresh to complete
              return this.enqueueRequest(() =>
                this.fetch(endpoint, { ...config, skipRefreshToken: true })
              );
            }

            this.isRefreshing = true;
            try {
              const newToken = await authService.refreshAccessToken();
              this.isRefreshing = false;

              if (newToken) {
                // Process all queued requests
                this.refreshQueue.forEach(callback => callback(newToken));
                this.refreshQueue = [];

                return this.fetch(endpoint, { ...config, skipRefreshToken: true });
              } else {
                // Clear queue on failure
                this.refreshQueue = [];

                // Safer check for window context before accessing location
                if (typeof window !== 'undefined') {
                  // Only clear data and redirect if not on password reset flow
                  if (!window.location.pathname.includes('/reset-password')) {
                    await authService.clearAllData();
                    window.location.href = '/login';
                  }
                }
                throw new Error('Session expired. Please login again.');
              }
            } catch (error) {
              this.isRefreshing = false;
              this.refreshQueue = [];
              throw error;
            }
          }

          throw new Error(errorData.message || 'Request failed');
        }

        // Safely parse JSON response, handling empty responses
        if (responseType === 'blob') {
          return await response.blob() as T;
        }

        const text = await response.text();
        let data = null;

        if (text) {
          try {
            data = JSON.parse(text); // Debug log
          } catch (e) {
            console.error('Error parsing JSON response:', e);
            throw new Error('Failed to parse response');
          }
        }

        // Force logout if in-memory user is inactive
        const inMemoryUser = authService.getUser();
        if (inMemoryUser && (inMemoryUser as any).status === 'inactive') {
          toast.error('Your account is inactive. Please contact admin.');
          console.log('User is inactive, logging out:', inMemoryUser); // Debug log
          await authService.logout();
        }

        
        return data;
      } catch (error: any) {
        if (isOffline && !isServiceWorker) {
          console.log(`[ApiInterceptor] Handling offline request error for: ${endpoint}`);
          // If we get here and we're offline, it means the service worker didn't intercept
          // We'll let the service worker handle the error
          if (error.message?.includes('Failed to fetch') ||
            error.message?.includes('NetworkError') ||
            error.message?.includes('ERR_INTERNET_DISCONNECTED')) {
            console.warn(`Network request failed due to being offline: ${endpoint}`);
          }
        }
        throw error;
      }
    };

    try {
      return await executeRequest();
    } catch (error) {
      console.error(`API request failed for ${endpoint}:`, error);
      throw error;
    }
  }
}

export const api = ApiInterceptor.getInstance(); 